import React, { useCallback, useEffect } from "react";

import clsx from "clsx";
import { saveAs } from "file-saver";
import { omit } from "lodash";
import moment from "moment";
import Papa from "papaparse";

import ChangeFractionDisplay from "@components/change-fraction";
import { useUser } from "@components/user-context";
import { useQueryMetadata } from "@components/v2/modals";
import { AnnotationsFlyoutButton } from "@features/custom-annotations";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchOrderSummaryGraph,
  type FetchOrderSummaryGraphVariables,
} from "@north-beam/nb-client-side";
import {
  APIGranularity,
  useOrderGraphFilters,
} from "@pages/orders/use-orders-filters";
import { type LegendMetric } from "@pages/sales-v2/components/chart";
import { FETCH_ORDER_SUMMARY_GRAPH } from "@pages/settings/queries";
import { SecondaryButton } from "@shared/buttons";
import { MetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import { useReportBodyState } from "./orders-control-state";

type ChartLegendProps = {
  legendMetrics: LegendMetric[];
  selectMetric: (index: number) => void;
  selectedMetricIndices: number[];
};

export const ChartLegend = ({
  legendMetrics,
  selectMetric,
  selectedMetricIndices,
}: ChartLegendProps) => {
  const { dateRange } = useReportBodyState();

  return (
    <div className="py-4">
      <div className="flex justify-between">
        <div data-test="sales-chart-legend-v2" className="flex flex-wrap">
          {legendMetrics.map((metric, index) => {
            const active = selectedMetricIndices.includes(index);
            const {
              currentValue,
              compareValue,
              hideComparisonFromTabDisplay,
              changeFractionAreaText,
              isPositiveChangeGood,
            } = metric;

            const changeFraction = (currentValue - compareValue) / compareValue;
            const positiveChange = changeFraction >= 0;
            const delta = hideComparisonFromTabDisplay
              ? undefined
              : {
                  changeFraction,
                  isPositiveChangeGood,
                };

            const hasChange =
              typeof changeFraction !== "undefined" &&
              changeFraction !== null &&
              !isNaN(changeFraction) &&
              delta;

            return (
              <button
                key={metric.name}
                className={clsx(
                  active && "border-2 border-primary",
                  "bg-white rounded-lg border border-gray",
                  "mb-2 mr-2 py-2 px-3 text-sm text-left",
                )}
                onClick={() => selectMetric(index)}
              >
                {metric.loading ? (
                  <div className="flex-center">
                    <Spinner />
                  </div>
                ) : (
                  <div>
                    <div className="font-semibold text-sm mb-0.5">
                      {metric.name}
                    </div>
                    <div className="text-xs flex items-center space-x-1 font-mono">
                      <MetricValue
                        value={currentValue}
                        precision="exact"
                        as="span"
                        format={metric.format}
                      />
                      <span>
                        {hasChange && (
                          <i
                            className={clsx(
                              "fa-regular fa-sharp",
                              positiveChange
                                ? {
                                    "fa-arrow-trend-up text-good":
                                      isPositiveChangeGood,
                                    "fa-arrow-trend-down text-bad":
                                      !isPositiveChangeGood,
                                  }
                                : {
                                    "fa-arrow-trend-up text-good":
                                      !isPositiveChangeGood,
                                    "fa-arrow-trend-down text-bad":
                                      isPositiveChangeGood,
                                  },
                            )}
                          />
                        )}
                      </span>
                      <div>
                        {changeFractionAreaText ? (
                          <small className="text-muted">
                            {changeFractionAreaText}
                          </small>
                        ) : delta ? (
                          <ChangeFractionDisplay {...delta} />
                        ) : (
                          <small>&nbsp; </small>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center space-x-2">
          <OrderChartExport />
          <AnnotationsFlyoutButton
            dateRange={dateRange}
            buttonClass="font-normal"
          />
        </div>
      </div>
    </div>
  );
};

export function dateFormat(
  date: string,
  granularity: APIGranularity,
  timezone: string,
) {
  let format: undefined | string;
  if (granularity === APIGranularity.HOUR) {
    format = undefined;
  } else if (granularity === APIGranularity.DAY) {
    format = "YYYY-MM-DD";
  } else if (granularity === APIGranularity.MONTH) {
    format = "YYYY-MM";
  } else {
    // else weekly
    format = "YYYY-MM-DD";
  }

  return moment.tz(date, timezone).format(format);
}

function OrderChartExport() {
  const { dateRange, granularity, ...restOfFilters } = useOrderGraphFilters();
  const { user } = useUser();
  const { addQueryMetadata } = useQueryMetadata();

  const { data: graphResponse, loading } = useNorthbeamQuery<
    FetchOrderSummaryGraph,
    FetchOrderSummaryGraphVariables
  >(FETCH_ORDER_SUMMARY_GRAPH, {
    variables: {
      filterOptions: omit(restOfFilters, "compareDateRange"),
      dateRange,
      granularity,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Orders",
        topic: "Orders Chart",
        metadata: data.me.orderSummaryGraph.queryMetadata,
      });
    },
  });

  const onExport = useCallback(() => {
    const data = graphResponse?.me.orderSummaryGraph.data;
    if (!data) {
      return;
    }

    const headers = ["Date", "Revenue", "Transactions"];
    const rows = data.map((point) => {
      return [
        dateFormat(point.datetime, granularity, user.timezone),
        point.orderRevenue,
        point.orderCount,
      ];
    });
    const csvString = Papa.unparse([headers, ...rows]);

    const blob = new Blob([csvString], {
      type: "text/plain;charset=utf-8",
    });

    saveAs(blob, "orders-chart.csv");
  }, [granularity, graphResponse?.me.orderSummaryGraph.data, user.timezone]);

  return (
    <>
      <SecondaryButton
        className="w-[10rem]"
        onClick={onExport}
        loading={loading}
      >
        EXPORT CHART DATA
      </SecondaryButton>
    </>
  );
}
